import { PointerEventTypes, type PointerInfo } from '@babylonjs/core/Events/pointerEvents';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import type { Scene } from '@babylonjs/core/scene';

// Outline rendering is a side-effect import, like the rest of Babylon's optional
// subsystems. Without it `mesh.renderOutline = true` is silently a no-op — a
// runtime nothing rather than a build error, which is the trap documented in
// `doc/grind/architecture/ENTITY-MODELS-ANIMATION.md`.
import '@babylonjs/core/Rendering/outlineRenderer';

import {
  GATE_EXIT,
  GATE_PORTAL,
  attackGround,
  attackTarget,
  breakFixture,
  castSkill,
  openStation,
  pickup,
  useGate,
  type GateKind,
  type StationKind,
} from '../net/protocol';
import { BREAKABLE_RADIUS } from './zone/fixtures';
import type { GameStore } from '../state/store';
import { LMB_SLOT, RMB_SLOT } from '../ui/actionbar';
import type { EntityMeshes } from './entities';
import type { Navigator } from './navigate';
import type { LootMeshes } from './loot';
import type { WorldObject } from './zone/render';

/**
 * The clickable furniture of the world: town portals and artisans.
 *
 * A narrow view of `ZoneRenderer`, so this file needs nothing from the zone geometry
 * but the two questions a pointer asks.
 */
export interface WorldObjects {
  objectForMesh(mesh: unknown): WorldObject | null;
  setObjectHighlight(key: string, on: boolean): void;
}

/**
 * How much closer than `stationRange` a walk to an artisan has to get before the shop
 * opens, in world units.
 *
 * The client is deliberately stricter than the server here, and that direction is the
 * whole point: the server measures from the station's tile centre with its own copy of
 * the player's position, which is up to a tick ahead of the predicted one. Arriving at
 * exactly the server's limit would open a panel the server then closed again — a flicker
 * that would look like the shop refusing at random.
 */
export const STATION_ARRIVAL_MARGIN = 0.75;

/**
 * How far a click on a barrel walks, before it asks to break it: inside the server's reach
 * (`breakFixture` in fixtures.go — the weapon's reach but never under `BREAK_FIXTURE_REACH`, plus the
 * barrel's radius) by this margin, for the reason a workshop's walk stops short of its range.
 */
export const BREAK_ARRIVAL_MARGIN = 0.4;
/** The least reach a barrel is broken from. Must equal `breakFixtureReach` in fixtures.go. */
export const BREAK_FIXTURE_REACH = 2.0;

/**
 * The same margin for a gate, and it exists for the same reason.
 *
 * The walk has to end *inside* the range the server measures, not on its edge: a
 * prediction a tick ahead of the server would otherwise send `CMD_USE_GATE` from a
 * position the server has not seen the player reach, and the answer would be
 * `OUT_OF_RANGE` for a gate the player is visibly standing on.
 */
export const GATE_ARRIVAL_MARGIN = 0.75;

/** Supplies the meshes that count as walkable ground, which change per zone. */
export type GroundSource = () => readonly AbstractMesh[];

/**
 * When a held drag re-issues its destination: it must have moved this far, and
 * this long must have passed.
 *
 * Both gates are needed, and the time one is doing most of the work. Holding the
 * button with the mouse perfectly still does *not* mean a fixed destination —
 * the camera follows the player, so the world point under a stationary cursor
 * slides ahead of them, which is exactly the "keep walking that way" behaviour
 * hold-to-move is for. Distance alone would therefore fire on every pointer
 * event, and a mouse reporting at 125 Hz would put 125 orders a second on the
 * socket, each burning a sequence number.
 *
 * 90 ms is roughly three server ticks — far finer than the player can perceive,
 * and about a tenth of the traffic.
 */
export const DRAG_RESEND_DISTANCE = 0.34;
export const DRAG_RESEND_INTERVAL_MS = 90;

/**
 * Whether a mesh may take a click.
 *
 * Babylon's default predicate requires `isVisible`, which would skip the
 * invisible hitboxes that entities and loot rely on — those exist precisely so a
 * click does not have to land on an animating rib or a spinning shard. Dropping
 * the visibility requirement is what makes them work; `isPickable` still gates
 * everything, and the visual meshes set it to false.
 *
 * Pure and exported so the rule is testable without a scene.
 */
export function pickable(mesh: { isPickable?: boolean; isEnabled?: () => boolean }): boolean {
  if (!mesh.isPickable) return false;
  return mesh.isEnabled ? mesh.isEnabled() : true;
}

/** What the cursor is currently over. */
export type HoverKind = 'mob' | 'loot' | 'object' | null;

/** The last destination a drag sent, and when. */
export interface DragState {
  x: number;
  z: number;
  at: number;
}

/**
 * True when a drag should re-issue its destination.
 *
 * Pure, so the throttle can be tested without a scene or a clock. The first
 * order of a drag always goes — there is nothing to compare it against, and it
 * is the one the player is waiting on.
 */
export function shouldResend(
  last: DragState | null,
  next: { x: number; z: number },
  now: number,
  minDistance: number = DRAG_RESEND_DISTANCE,
  minInterval: number = DRAG_RESEND_INTERVAL_MS,
): boolean {
  if (!last) return true;
  if (now - last.at < minInterval) return false;
  return Math.hypot(next.x - last.x, next.z - last.z) >= minDistance;
}

/**
 * Turns pointer input into intents.
 *
 * Three jobs, and they are separate on purpose:
 *
 *  - **Hover** highlights whatever is under the cursor, so it is obvious before
 *    clicking that the click will connect. The colliders are invisible by
 *    design, which otherwise leaves the player guessing where a mob ends.
 *  - **Press and hold** walks toward the cursor and keeps walking as it moves —
 *    click-to-move alone means one click per step across a long room.
 *  - **Click** targets a mob or grabs a drop.
 *
 * This never moves anything itself. Movement is a `CMD_MOVE_TO` plus a local
 * prediction; where the player actually ends up comes back from the server. See
 * `doc/grind/architecture/SYSTEM-OVERVIEW.md`.
 */
/**
 * Whether a right click on `target` should open the browser's own menu: in a text field (paste,
 * spelling) and in anything marked `data-context-menu="allow"` — the chat log. Everywhere else in
 * the game it is suppressed, because the right button is a skill slot (ADR 0005).
 */
export function allowsContextMenu(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return target.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"], [data-context-menu="allow"]') !== null;
}

export function attachInput(
  scene: Scene,
  ground: GroundSource,
  meshes: EntityMeshes,
  loot: LootMeshes,
  /**
   * Routes movement round walls. Every walk order goes through it, and so does
   * every stop — an attack that only halted the prediction would leave a route
   * still feeding legs to the server.
   */
  navigator: Navigator,
  /**
   * The world's clickable furniture: gates and artisans.
   *
   * **Each of these is a movement order plus an intent, never a second way to do a
   * thing.** Clicking a gate walks to it and steps through on arrival; clicking an
   * artisan walks to their counter and opens their shop. The server re-checks the
   * distance in both cases, so the click names an intent and never an outcome.
   */
  objects: WorldObjects,
  store: GameStore,
  /** Injectable clock, so the drag throttle is testable without real time. */
  clock: () => number = () => performance.now(),
): () => void {
  /**
   * Collects a drop: walk into `PickupRange`, then take it — the one path for a click on the item
   * and a click on its label (`store.collect`, wired just below).
   *
   * Routed through the `Navigator` for the same reason the chase and the gates are: predicting the
   * movement, sending it and dropping the route when something else takes over all happen in one
   * place. The arrival radius is the server's own `PickupRange`, so the walk ends exactly where the
   * rule starts being true.
   */
  const collect = (lootId: string) => {
    const at = store.groundAt(lootId);
    if (at) {
      store.setTarget(null);
      navigator.goTo(at.x, at.z, {
        at,
        radius: store.pickupRange(),
        done: () => store.send(pickup(lootId)),
      });
    } else {
      // Not in the snapshot the client holds — somebody else took it a moment ago. Asking anyway
      // is harmless: the server refuses an unknown drop.
      store.send(pickup(lootId));
    }
  };
  store.onCollect(collect);

  /** Overlapping drops resolve to the one under the pointer. Optional so test fakes need not have it. */
  const resolveLoot = (id: string): string =>
    (loot as Partial<Pick<LootMeshes, 'resolveUnderPointer'>>).resolveUnderPointer?.(id, scene.pointerX, scene.pointerY) ?? id;

  // Widen the pick predicate so invisible hitboxes are eligible. All three are
  // set: a down/up pair drives clicks and moves drive hover, and each uses its
  // own predicate.
  const previousDown = scene.pointerDownPredicate;
  const previousUp = scene.pointerUpPredicate;
  const previousMove = scene.pointerMovePredicate;
  scene.pointerDownPredicate = pickable;
  scene.pointerUpPredicate = pickable;
  scene.pointerMovePredicate = pickable;

  // **Without this, POINTERMOVE arrives with no `pickInfo` at all.**
  //
  // Babylon skips the pick on pointer move as an optimisation unless something
  // asks for it — its own check is `onPointerObservable.observers.length > 1`,
  // and this scene has exactly one observer. The result is a hover that silently
  // never fires: the handler runs, `info.pickInfo` is null, and nothing
  // highlights. This is the flag that asks for it.
  const previousConstantUpdate = scene.constantlyUpdateMeshUnderPointer;
  scene.constantlyUpdateMeshUnderPointer = true;

  const canvas = scene.getEngine().getRenderingCanvas();

  /** Held while the button is down over the floor. */
  let dragging = false;
  /** The last destination sent and when, so a hold does not flood the socket. */
  let lastSent: DragState | null = null;

  let hoveredEntity: string | null = null;
  let hoveredLoot: string | null = null;
  let hoveredObject: string | null = null;

  const clearHover = () => {
    if (hoveredEntity !== null) meshes.setHighlight(hoveredEntity, false);
    if (hoveredLoot !== null) loot.setHighlight(hoveredLoot, false);
    if (hoveredObject !== null) objects.setObjectHighlight(hoveredObject, false);
    hoveredEntity = null;
    hoveredLoot = null;
    hoveredObject = null;
    // The HUD's copy. A trash mob's name is drawn on hover now, so this has to be published
    // rather than kept in the closure — see `plateVerdict`.
    store.setHoveredEntityId(null);
    if (canvas) canvas.style.cursor = '';
  };

  /** Highlights whatever the cursor is over, and nothing else. */
  const setHover = (kind: HoverKind, id: string | null) => {
    const nextEntity = kind === 'mob' ? id : null;
    const nextLoot = kind === 'loot' ? id : null;
    const nextObject = kind === 'object' ? id : null;
    if (
      nextEntity === hoveredEntity &&
      nextLoot === hoveredLoot &&
      nextObject === hoveredObject
    ) {
      return;
    }

    if (hoveredEntity !== null && hoveredEntity !== nextEntity) {
      meshes.setHighlight(hoveredEntity, false);
    }
    if (hoveredLoot !== null && hoveredLoot !== nextLoot) {
      loot.setHighlight(hoveredLoot, false);
    }
    if (hoveredObject !== null && hoveredObject !== nextObject) {
      objects.setObjectHighlight(hoveredObject, false);
    }

    hoveredEntity = nextEntity;
    store.setHoveredEntityId(nextEntity);
    hoveredLoot = nextLoot;
    hoveredObject = nextObject;

    if (hoveredEntity !== null) meshes.setHighlight(hoveredEntity, true);
    if (hoveredLoot !== null) loot.setHighlight(hoveredLoot, true);
    if (hoveredObject !== null) objects.setObjectHighlight(hoveredObject, true);

    if (canvas) canvas.style.cursor = kind === null ? '' : 'pointer';
  };

  /** Sends a walk order, routing round walls and predicting locally first. */
  const walkTo = (x: number, z: number, now: number) => {
    navigator.goTo(x, z);
    // The throttle tracks where the player *asked* to go, not the first leg of
    // the route — a drag whose destination barely moves must not re-path just
    // because the leg it is currently walking changed.
    lastSent = { x, z, at: now };
  };

  /**
   * Walks to a gate or an artisan, and arranges whatever arriving means.
   *
   * **A click is a movement order plus an intent**, and both halves are the same shape
   * for all three kinds now. A gate used to be the exception: it was entered by *standing
   * in it*, checked server-side at the end of every `Step`, so the click only started a
   * walk and the arrival did the rest by itself. That made a gate impossible to stand
   * beside — the town's entry had to sit at the far end of the street from its portals —
   * and gave a player no way to say *not yet*. `CMD_USE_GATE` is sent on arrival now, and
   * the server measures the distance exactly as it does for a workshop.
   *
   * Nothing here is answered locally. A workshop's panel opens on arrival because the
   * client already holds the answer; a gate's outcome is a whole new zone, which only the
   * server can produce — so the walk ends in a command and the `ZONE` frame is the reply.
   */
  const walkToObject = (object: WorldObject) => {
    // A door: walk through it. The server opens a door a player leans on, so the walk *is* the
    // opening, and a click on one is a click on the doorway (`design/LEVEL-FIXTURES.md`).
    if (object.kind === 'door') {
      navigator.goTo(object.at.x, object.at.z);
      return;
    }
    // A barrel: walk into reach, then break it. Like a workshop, measured against the point the
    // server measures from.
    if (object.kind === 'breakable') {
      const reach = Math.max(store.attackRange(), BREAK_FIXTURE_REACH) + BREAKABLE_RADIUS - BREAK_ARRIVAL_MARGIN;
      // Walk to the near side of the barrel, not its centre: the walk outlives the arrival, and a
      // destination at the centre carried the player on into the barrel's spot once it broke.
      const here = navigator.position;
      const dx = here.x - object.at.x;
      const dz = here.z - object.at.z;
      const away = Math.hypot(dx, dz) || 1;
      const stand = Math.min(away, BREAKABLE_RADIUS + 0.9);
      const approach = { x: object.at.x + (dx / away) * stand, z: object.at.z + (dz / away) * stand };
      navigator.goTo(approach.x, approach.z, {
        at: object.at,
        radius: Math.max(1, reach),
        done: () => store.send(breakFixture(Number(object.id))),
      });
      return;
    }

    // The town's [ GRIND ] gate. **Not a gate**: it opens the level select, which is a
    // client panel, so this is answered locally exactly as a workshop's is and nothing is
    // sent. The server has no command for "show me the choices".
    if (object.kind === 'grind') {
      navigator.goTo(object.approach.x, object.approach.z, {
        at: object.at,
        radius: Math.max(1, store.exitRange() - GATE_ARRIVAL_MARGIN),
        done: () => store.openMenu('portal'),
      });
      return;
    }

    if (object.kind === 'portal' || object.kind === 'exit') {
      const gate: GateKind = object.kind === 'exit' ? GATE_EXIT : GATE_PORTAL;
      navigator.goTo(object.approach.x, object.approach.z, {
        // Measured against the point the *server* measures, and against the range it
        // uses for that gate — `ExitRange` is more generous than `PortalEntryRange`, so
        // one number for both would either ask too early at the exit or walk needlessly
        // far into a portal.
        at: object.at,
        radius: Math.max(
          1,
          (gate === GATE_EXIT ? store.exitRange() : store.portalEntryRange()) - GATE_ARRIVAL_MARGIN,
        ),
        done: () => store.send(useGate(gate)),
      });
      return;
    }

    const kind = object.id as StationKind;
    navigator.goTo(object.approach.x, object.approach.z, {
      // Measured against the point the *server* measures — the station's tile centre —
      // rather than against the spot being walked to, so the two halves are asking the
      // same question. Tighter than the server's own limit, so a prediction a tick ahead
      // cannot open a panel the server then closes.
      at: object.at,
      radius: Math.max(1, store.stationRange() - STATION_ARRIVAL_MARGIN),
      done: () => {
        // Answered locally and still sent — the rule a predicted dodge follows. The
        // panel opens now, because a round trip for an answer the client already holds
        // is the friction the hub spec exists to remove, and `EVENT_STATION_REFUSED` is
        // the server closing it again if the two disagree.
        store.openStation(kind);
        store.send(openStation(kind));
      },
    });
  };

  /** True while world input should be ignored. */
  const suspended = () => store.menuOpen() || store.phase() !== 'playing';

  /**
   * The skill the **left button** attacks with, or `null` for the basic attack.
   *
   * Added 2026-08-12 (*LMB should allow to assign any skills*). `LMB_SLOT` is the only
   * place that says the left button owns a slot; the server sees an index and has no
   * opinion about which input sent it.
   *
   * `null` — nothing assigned — is the default and means every path below behaves exactly
   * as it did: target, chase, swing.
   */
  const lmbSkill = (): string | null => store.slotted()[LMB_SLOT] || null;

  const observer = scene.onPointerObservable.add((info: PointerInfo) => {
    // A full-screen panel suspends world input, per HUD-UX.md §3.
    // Without this, the click that dismisses the vault would also walk you
    // across the map.
    if (suspended()) {
      dragging = false;
      clearHover();
      return;
    }

    switch (info.type) {
      case PointerEventTypes.POINTERDOWN: {
        const pick = info.pickInfo;
        if (!pick?.hit || !pick.pickedMesh) return;

        if (pick.pickedPoint) {
          store.setCursorWorld({ x: pick.pickedPoint.x, z: pick.pickedPoint.z });
        }

        // **The right button casts the mouse slot, and never walks.**
        //
        // The genre's arrangement, and the reason it is worth a slot of its own rather
        // than an alias for one of the numbers: the left button is the basic attack, so
        // the right one is where a player expects their most-used skill to live, and a
        // hand already on the mouse should not have to reach for a number to use it.
        //
        // Aimed at the point under the cursor, exactly like a number key — the server
        // resolves what is standing there. It deliberately does **not** target, walk or
        // chase: a cast is an act at a place, and a right button that also moved you would
        // be the shift-click problem with no way to opt out of it.
        //
        // `RMB_SLOT` is the only place that says which index the mouse owns. The server
        // sees an index and has no opinion, which is what keeps this a single copy.
        if ((info.event as PointerEvent | undefined)?.button === 2) {
          const at = pick.pickedPoint;
          store.send(castSkill(RMB_SLOT, at ? { x: at.x, z: at.z } : undefined));
          navigator.stop();
          dragging = false;
          return;
        }

        // **Shift means "act where I am pointing, and do not walk."**
        //
        // The Diablo convention, and the reason it exists: standing on the edge of
        // a pack and clicking a monster behind it walks you into the middle of
        // them. Holding shift attacks or casts toward the cursor from where you
        // stand — the one input that lets a player fight without committing to a
        // move.
        const held = (info.event as PointerEvent | undefined)?.shiftKey === true;
        if (held) {
          const at = pick.pickedPoint;
          const mobUnder = meshes.idForMesh(pick.pickedMesh);
          // **A bound left button casts instead of swinging.** Shift already means *act
          // where I am pointing and do not walk*, which is what a cast is — so the only
          // thing the assignment changes here is which act.
          if (lmbSkill()) {
            if (mobUnder !== null && meshes.isHostile(mobUnder)) store.setTarget(mobUnder);
            store.send(castSkill(LMB_SLOT, at ? { x: at.x, z: at.z } : undefined));
          } else if (mobUnder !== null && meshes.isHostile(mobUnder)) {
            // A monster under the cursor is still the better target than a point.
            store.setTarget(mobUnder);
            store.send(attackTarget(mobUnder));
          } else if (at) {
            // Nothing under the cursor: aim the basic attack at whatever is
            // nearest that point, which the server resolves.
            store.send(attackGround(at.x, at.z));
          }
          navigator.stop();
          return;
        }

        // A dropped item: collect it. Checked before mobs, because loot beams
        // can overlap a corpse and grabbing the drop is what the player meant.
        const pickedLoot = loot.idForMesh(pick.pickedMesh);
        // The drop drawn nearest the pointer, not the box nearest the camera — see
        // `LootMeshes.resolveUnderPointer`. Hover asks the same, so the outlined item is the one taken.
        const lootId = pickedLoot === null ? null : resolveLoot(pickedLoot);
        if (lootId !== null) {
          // **Walk to it, then take it.**
          //
          // This used to send `CMD_PICKUP` outright, which the server refused unless the
          // player already happened to be standing on the drop — so clicking an item across
          // the room did nothing at all, and the only way to collect was to walk over by
          // hand first. Reported directly: *clicking on the loot does not move to it, I'd
          // rather the player actually move to it.*
          //
          // Routed through the `Navigator` for the same reason the chase and the gates are:
          // predicting the movement, sending it and dropping the route when something else
          // takes over all have to happen in one place. The arrival radius is the server's
          // own `PickupRange`, so the walk ends exactly where the rule starts being true.
          collect(lootId);
          return;
        }

        // A mob: target it, walk into range, and swing.
        //
        // **This is the click that changed.** It used to send `CMD_ATTACK_TARGET` and
        // halt the prediction, which was correct when the server stopped movement on an
        // attack and never chased: clicking a monster across the room targeted it and
        // then did nothing at all, and the player had to walk over by hand. The chase is
        // client-side for the same reason routing is — a server that walked the player
        // toward a target would correct the predictor every frame — and it goes through
        // the Navigator because predicting, sending and dropping the route have to
        // happen in one place.
        const id = meshes.idForMesh(pick.pickedMesh);
        if (id !== null && meshes.isHostile(id)) {
          store.setTarget(id);

          // ── A bound left button casts, and does not chase ──────────────────────
          //
          // The same rule the right button has followed since it got a slot: *a cast is an
          // act at a place.* Aimed at the monster's position, from where the player stands,
          // and the server resolves what is standing there.
          //
          // **Named limitation, chosen not overlooked:** an empty left button chases — walks
          // into reach and swings — and a bound one does not. Chasing with a skill would mean
          // the client knowing that skill's range to know where to stop walking, and range is
          // the server's (`attackRange()` is the weapon's, which is a different number). So a
          // player who assigns a melee skill to LMB walks with clicks on the ground and fights
          // with clicks on monsters, exactly as they already do on the right button.
          //
          // The default is unaffected, which is the reason this is shippable as it stands:
          // nothing chases differently until a player chooses to put a skill here.
          if (lmbSkill()) {
            const where = store.entityAt(id) ?? pick.pickedPoint;
            store.send(castSkill(LMB_SLOT, where ? { x: where.x, z: where.z } : undefined));
            navigator.stop();
            return;
          }

          const at = store.entityAt(id);
          if (at) {
            navigator.chase(id, at, clock());
          } else {
            // Not in the snapshot the client holds — a pick against a mesh whose entity
            // has just left it. Fall back to the old behaviour rather than chasing
            // something with no position: the server refuses an unknown target anyway.
            store.send(attackTarget(id));
            navigator.stop();
          }
          return;
        }

        // A town portal or an artisan. Below monsters, deliberately: a fight is the more
        // urgent of the two and a gate opened in the middle of a pack must not steal the
        // click that was meant for the thing killing you.
        const object = objects.objectForMesh(pick.pickedMesh);
        if (object !== null) {
          store.setTarget(null);
          walkToObject(object);
          return;
        }

        // The zone floor: walk there, and keep walking while the button is held.
        // The floor is several merged meshes, one per shade, so this is a
        // membership test rather than an identity check.
        if (pick.pickedPoint && ground().includes(pick.pickedMesh)) {
          store.setTarget(null);
          dragging = true;
          walkTo(pick.pickedPoint.x, pick.pickedPoint.z, clock());
        }
        return;
      }

      case PointerEventTypes.POINTERMOVE: {
        const pick = info.pickInfo;
        const mesh = pick?.hit ? pick.pickedMesh : null;

        // Remember where the pointer is in the world, so a number key can aim a
        // cast at it. Any surface will do — a point on a wall or a monster is
        // still the direction the player is pointing.
        if (pick?.pickedPoint) {
          store.setCursorWorld({ x: pick.pickedPoint.x, z: pick.pickedPoint.z });
        }

        // Hover, whether or not a drag is in progress — the highlight should not
        // freeze just because the player is walking.
        if (!mesh) {
          setHover(null, null);
        } else {
          const pickedLoot = loot.idForMesh(mesh);
          const lootId = pickedLoot === null ? null : resolveLoot(pickedLoot);
          const id = lootId === null ? meshes.idForMesh(mesh) : null;
          const object = lootId === null && id === null ? objects.objectForMesh(mesh) : null;
          if (lootId !== null) {
            setHover('loot', lootId);
          } else if (id !== null) {
            // Same order as the click, and the same rule: only a monster outlines.
            setHover(meshes.isHostile(id) ? 'mob' : null, id);
          } else if (object !== null) {
            setHover('object', object.key);
          } else {
            setHover(null, null);
          }
        }

        if (!dragging || !pick?.pickedPoint || !mesh) return;
        // Only the floor steers a drag. Sweeping the cursor over a mob mid-walk
        // must not stop the player dead, and must not attack either — that is
        // what a deliberate click is for.
        if (!ground().includes(mesh)) return;

        const next = { x: pick.pickedPoint.x, z: pick.pickedPoint.z };
        const now = clock();
        if (shouldResend(lastSent, next, now)) walkTo(next.x, next.z, now);
        return;
      }

      case PointerEventTypes.POINTERUP: {
        dragging = false;
        lastSent = null;
        return;
      }
    }
  });

  // A pointer that leaves the window never sends POINTERUP, which would leave
  // the player walking toward a cursor that is no longer there.
  const onLeave = () => {
    dragging = false;
    lastSent = null;
    clearHover();
  };
  canvas?.addEventListener('pointerleave', onLeave);

  // **The whole game page, not only the canvas** (2026-09-28). Reported: *sometimes, right click
  // still opens contextual browser window* — the canvas was the only thing guarded, and the play
  // area is covered in DOM the button lands on: loot labels, nameplates, damage numbers, panels.
  // A right click there opened the browser's menu over the game. What keeps it is only what uses
  // it (`allowsContextMenu`): a text field, and the chat log a name is copied out of.
  const onContextMenu = (event: Event) => {
    if (!allowsContextMenu(event.target)) event.preventDefault();
  };
  // And on the canvas itself, which is only reached through the page once it is in one.
  document.addEventListener('contextmenu', onContextMenu);
  canvas?.addEventListener('contextmenu', onContextMenu);

  return () => {
    scene.onPointerObservable.remove(observer);
    canvas?.removeEventListener('pointerleave', onLeave);
    document.removeEventListener('contextmenu', onContextMenu);
    canvas?.removeEventListener('contextmenu', onContextMenu);
    clearHover();
    scene.pointerDownPredicate = previousDown;
    scene.pointerUpPredicate = previousUp;
    scene.pointerMovePredicate = previousMove;
    scene.constantlyUpdateMeshUnderPointer = previousConstantUpdate;
  };
}
